import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect, useContext, createContext } from 'react';

import { Language } from 'types';
import { useAppSelector } from 'store';
import { getLangNameFromCode } from 'utils';
import { selectAuthUser } from 'store/slices/auth';

import {
  TranslationContextProps,
  TranslationContextProviderProps,
} from './types';

export const defaultLanguage = 'en';

const TranslationContext = createContext<TranslationContextProps>({
  languages: [],
  changeLanguage: () => {},
  language: defaultLanguage,
});

export const TranslationContextProvider = ({
  course,
  children,
}: TranslationContextProviderProps) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState<string>(i18n.language);
  const user = useAppSelector(selectAuthUser);

  const courseLanguagesKey = course?.languages?.join(',') ?? '';
  const tenantLanguagesKey = user?.tenant?.languages?.join(',') ?? '';

  const languages = useMemo(() => {
    const courseLanguages = [
      ...Array.from(
        new Set([
          ...(course?.languages ?? []),
          ...(user?.tenant?.languages ?? []),
        ])
      ),
    ];
    if (!courseLanguages.includes(defaultLanguage)) {
      courseLanguages.splice(0, 0, defaultLanguage);
    }
    return courseLanguages.map((code): Language => ({
      code,
      name: getLangNameFromCode(code)?.native || code,
    }));
  }, [courseLanguagesKey, tenantLanguagesKey]);

  useEffect(() => {
    if (!user) {
      return;
    }
    const lang = languages.find((lang) => lang.code === currentLanguage);
    if (!lang) {
      setCurrentLanguage(defaultLanguage);
      i18n.changeLanguage(defaultLanguage);
    }
  }, [user, currentLanguage, languages, i18n]);

  return (
    <TranslationContext.Provider
      value={{
        languages,
        language: currentLanguage,
        changeLanguage: (lang) => {
          setCurrentLanguage(lang);
          i18n.changeLanguage(lang);
        },
      }}
    >
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslationContext = () => {
  return useContext(TranslationContext);
};
